<script lang="ts">
	/**
	 * Renders the bulk-actions section (Update FW, Move to room, Delete, AI
	 * commands…) as `MenuItemList` entries, intended to be dropped as children
	 * of a parent `DropdownMenu` in `AllDevicesDropdown`. Owns nothing that
	 * belongs to the trigger — the parent controls open state, close, and the
	 * mobile options-modal path.
	 *
	 * Menu data is built in the parent so it stays available for the mobile
	 * modal even when `DropdownMenu` keeps its slot unmounted (`show === false`).
	 */
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { DropdownMenu, MenuItemList } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { firstCharCapOnly } from 'src/helpers/utilities';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { devices } from 'src/stores';
	import {
		initialEdit,
		type UpdateModeSubTypes,
		type UpdateModeTypes,
	} from 'src/stores/ui/edit_lists';

	const dispatch = createEventDispatcher<{ 'item-click': void }>();

	export let sectionTitle: string = '';
	export let dropdownMenuMenuData: any[] = [];
	export let onChangeClick: (type: UpdateModeTypes, subType?: UpdateModeSubTypes) => void = () => {};

	let windowClientWidth: number;

	$: isEmptyDeviceList = Object.values($devices).length === 0;

	const clearSubmenuStates = () => {
		dropdownMenuMenuData.forEach((item: any) => {
			if (item?.hasOwnProperty('showDropdown')) item.showDropdown = false;
		});
		dropdownMenuMenuData = dropdownMenuMenuData;
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#if sectionTitle}
	<MenuItemList isGroupTitle size="m" title={sectionTitle} />
{/if}

{#each dropdownMenuMenuData as dropdownSubmenuItem, i}
	{#if dropdownSubmenuItem.hasOwnProperty('showDropdown')}
		<DropdownMenu
			class="width-full"
			bind:show={dropdownSubmenuItem.showDropdown}
			positionHorizontally
			on:close={() => {
				dropdownMenuMenuData[i].active = false;
			}}
		>
			<MenuItemList
				hasBorder
				title={dropdownSubmenuItem.title ===
				$format('sub_settings_title_ai_model', { default: 'AI Model' })
					? dropdownSubmenuItem.title
					: firstCharCapOnly(dropdownSubmenuItem.title)}
				icon={dropdownSubmenuItem.icon}
				rightIcon={dropdownSubmenuItem.rightIcon}
				active={dropdownSubmenuItem.active}
				slot="trigger"
				on:mouseenter={() => {
					dropdownMenuMenuData[i].showDropdown = true;
					dropdownMenuMenuData[i].active = true;
				}}
				on:click={() => {
					if (windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX) {
						dropdownSubmenuItem.showDropdown = !dropdownSubmenuItem.showDropdown;
					}
				}}
			/>
			{#each dropdownSubmenuItem.items as submenuItem}
				<MenuItemList
					title={submenuItem.title}
					icon={submenuItem.icon}
					on:click={() => {
						if (submenuItem.id === 9 || submenuItem.id === 10 || submenuItem.id === 11) {
							onChangeClick('ai_device_failure', submenuItem.aiSubtype);
						}
						clearSubmenuStates();
						dropdownMenuMenuData[i].showDropdown = false;
						dropdownMenuMenuData[i].active = false;
						dispatch('item-click');
					}}
				/>
			{/each}
		</DropdownMenu>
	{:else}
		<MenuItemList
			title={firstCharCapOnly(dropdownSubmenuItem.title)}
			icon={dropdownSubmenuItem.icon}
			on:mouseenter={() => clearSubmenuStates()}
			on:click={() => {
				resetDrawer();
				dropdownSubmenuItem.callback?.();
				initialEdit.set(false);
				dispatch('item-click');
			}}
			disabled={isEmptyDeviceList}
		/>
	{/if}
{/each}
